import request from '@/api/request'
import type { CareSidebarCounts } from '@/api/hmCareOrderTypes'

/** POST `/api/admin/care-order/sidebar-stats` 解析结果 */
export type CareOrderSidebarStats = CareSidebarCounts & {
  /** HM Care Orders 主目录角标（接口 total / hm_* 字段） */
  hmCareOrdersTotal: number
}

function readNonNegInt(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return Math.floor(raw)
  if (typeof raw === 'string' && raw.trim()) {
    const n = Number(raw.trim())
    if (Number.isFinite(n) && n >= 0) return Math.floor(n)
  }
  return null
}

function pickCount(source: Record<string, unknown>, keys: string[]): number {
  for (const key of keys) {
    const n = readNonNegInt(source[key])
    if (n != null) return n
  }
  return 0
}

/** 展开 data / sidebar_counts 等嵌套对象，便于兼容不同字段布局 */
function flattenStatsPayload(raw: unknown): Record<string, unknown> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const root = raw as Record<string, unknown>
  const out: Record<string, unknown> = { ...root }
  for (const nestKey of [
    'data',
    'counts',
    'stats',
    'sidebar',
    'sidebar_counts',
    'sidebarCounts',
    'sidebar_stats',
    'sidebarStats',
  ]) {
    const nest = root[nestKey]
    if (nest && typeof nest === 'object' && !Array.isArray(nest)) {
      Object.assign(out, nest as Record<string, unknown>)
    }
  }
  return out
}

/**
 * 解析 sidebar-stats 响应。
 * 兼容 snake_case / camelCase，以及 `{ code, data }` 包一层或直接 data 对象。
 */
export function parseCareOrderSidebarStatsResponse(raw: unknown): CareOrderSidebarStats {
  const envelope =
    raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : null
  const payload = flattenStatsPayload(
    envelope && 'data' in envelope && envelope.data != null ? envelope.data : raw,
  )

  return {
    pendingSchedule: pickCount(payload, [
      'pending_schedule',
      'pendingSchedule',
      'pend_sched',
      'pendSched',
    ]),
    pendingReschedule: pickCount(payload, [
      'pending_reschedule',
      'pendingReschedule',
      'pend_resched',
      'pendResched',
    ]),
    fuPendingSchedule: pickCount(payload, [
      'fu_pending_schedule',
      'fuPendingSchedule',
      'fu_pend_sched',
      'fuPendSched',
    ]),
    fuPendingReschedule: pickCount(payload, [
      'fu_pending_reschedule',
      'fuPendingReschedule',
      'fu_pend_resched',
      'fuPendResched',
    ]),
    hmCareOrdersTotal: pickCount(payload, [
      'total',
      'hm_care_orders',
      'hmCareOrders',
      'hm_orders',
      'hmOrders',
      'hm_care_orders_total',
      'hmCareOrdersTotal',
      'all',
    ]),
  }
}

/** POST `/api/admin/care-order/sidebar-stats` — 侧栏四桶角标 + HM 总数 */
export async function postCareOrderSidebarStats(): Promise<CareOrderSidebarStats> {
  const raw = await request.post('/api/admin/care-order/sidebar-stats', {})
  if (raw != null && typeof raw === 'object') {
    const r = raw as Record<string, unknown>
    if (typeof r.code === 'number' && r.code !== 200) {
      const msg =
        (typeof r.msg === 'string' && r.msg.trim()) ||
        (typeof r.message === 'string' && r.message.trim()) ||
        'Failed to load sidebar stats.'
      throw new Error(msg)
    }
  }
  return parseCareOrderSidebarStatsResponse(raw)
}
